import React, { useState, useEffect, useRef } from "react";
import "./AnimatedMicrophone.css";

export default function AnimatedMicrophone({
  isAudioOnGoing,
  onMicToggle,
  isEnabled = true,
  showVolumeIndicator = true,
  size = 28
}) {
  const [audioLevel, setAudioLevel] = useState(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const streamRef = useRef(null);

  const startListening = async () => {
    if (!isEnabled || !isAudioOnGoing) {
      console.log('🔇 Not starting listening - isEnabled:', isEnabled, 'isAudioOnGoing:', isAudioOnGoing);
      return;
    }

    try {
      console.log('🎤 Starting microphone listening...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);

      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      const analyzeAudio = () => {
        if (!analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        const average =
          dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        const normalizedLevel = Math.min(average / 50, 1);

        setAudioLevel(normalizedLevel);
        animationFrameRef.current = requestAnimationFrame(analyzeAudio);
      };

      analyzeAudio();
      console.log('🎤 Microphone listening started successfully');
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopListening = () => {
    console.log('🔇 Stopping microphone listening...');
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setAudioLevel(0);
    console.log('🔇 Microphone listening stopped');
  };

  useEffect(() => {
    if (isAudioOnGoing && isEnabled) {
      startListening();
    } else {
      stopListening();
    }

    return () => {
      stopListening();
    };
  }, [isAudioOnGoing, isEnabled]);

  // Also stop listening when component unmounts or when audio is muted
  useEffect(() => {
    if (!isAudioOnGoing) {
      stopListening();
    }
  }, [isAudioOnGoing]);

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, []);

  const isOn = Boolean(isEnabled && isAudioOnGoing);

  return (
    <div
      className={`mic-anim ${isOn ? "mic-anim--on" : ""}`}
      style={{ "--level": audioLevel, "--icon": `${size}px` }}
      // onClick={onMicToggle}
      role="button"
      aria-pressed={isOn}
      title={isOn ? "Mute" : "Unmute"}
    >
      {/* <div className="mic-anim__icon" aria-hidden="true">
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 15c1.657 0 3-1.343 3-3V6c0-1.657-1.343-3-3-3S9 4.343 9 6v6c0 1.657 1.343 3 3 3Z" fill="currentColor"/>
          <path d="M5 11a1 1 0 1 0-2 0c0 4.08 3.053 7.436 7 7.93V22a1 1 0 1 0 2 0v-3.07c3.947-.495 7-3.85 7-7.93a1 1 0 1 0-2 0c0 3.309-2.691 6-6 6s-6-2.691-6-6Z" fill="currentColor"/>
        </svg>
      </div> */}

      <div className="mic-anim__indicator" aria-hidden={!showVolumeIndicator}>
        <div className="mic-anim__flip">
          <div className="mic-anim__face mic-anim__face--dot">
            <span className="mic-anim__dot" />
          </div>
          <div className="mic-anim__face mic-anim__face--bars">
            <div className="mic-anim__bars">
              <i style={{ "--bar": 0.9, animationDelay: "0ms" }} />
              <i style={{ "--bar": 1.2, animationDelay: "80ms" }} />
              <i style={{ "--bar": 1.6, animationDelay: "120ms" }} />
              <i style={{ "--bar": 1.1, animationDelay: "60ms" }} />
              <i style={{ "--bar": 0.8, animationDelay: "100ms" }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
